import {
  Component,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
} from '@angular/core';

import { toObservable } from '@angular/core/rxjs-interop';
import { translate } from '@jsverse/transloco';
import { skip, Subject, takeUntil } from 'rxjs';
import { VerticalTab } from '@inmoment/the-kitchen';

import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ResponsiveService } from 'app/services/responsive.service';
import { RealtimeService } from '../../../providers/realtime.service';

interface SidePanelTabData extends VerticalTab {
  leftSideTemplate?: TemplateRef<any>;
  rightSideTemplate: TemplateRef<any>;
  features?: string[];
  adminOnly?: boolean;
  permissions?: string[];
  executiveFeatures?: string[];
  disabledReasonKey?: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-lex-xi-side-panel',
  templateUrl: './lex-xi-side-panel.component.html',
  styleUrls: ['./lex-xi-side-panel.component.scss'],
  standalone: false,
})
export class LexXiSidePanelComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  readonly isLoading = input(true);
  readonly selectedItem = input<unknown>();
  readonly rightPlaceholder = input<{ title: string; subtitle?: string }>();
  readonly panelOpen = model(true);
  readonly isInterceptEnabled = input(false);
  readonly verticalTabBarData = input<SidePanelTabData[]>();

  readonly open = output();
  readonly close = output();

  readonly activeTab = signal<SidePanelTabData | undefined>(undefined);
  readonly filteredTabBarData = signal<SidePanelTabData[]>([]);
  rtrConnected$ = this.realTimeService.getConnectionStatus();
  private verticalTabBarData$ = toObservable(this.verticalTabBarData);

  private accountService = inject(SemantriaAccount);
  private responsiveService = inject(ResponsiveService);
  private authService = inject(AuthService);
  private groupService = inject(GroupService);

  private readonly destroy$ = new Subject<void>();
  private account?: { features?: string[] };

  constructor(private realTimeService: RealtimeService) {}

  async ngOnInit(): Promise<void> {
    this.responsiveService.setIsSidePanelOpen(this.panelOpen());
    this.account = await this.accountService.sub.latest();
    this.refreshTabBarData();

    this.verticalTabBarData$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.refreshTabBarData());

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshTabBarData());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshTabBarData(): void {
    const tabData = this.verticalTabBarData();
    if (!this.account || !tabData?.length) {
      this.filteredTabBarData.set([]);
      this.activeTab.set(undefined);
      return;
    }

    const isAdmin = this.authService.isAccountAdmin;
    const isExecutive = this.authService.isExecutive;
    const accountFeatures = this.account.features ?? [];

    const filtered = tabData
      .filter((d) => !d.adminOnly || isAdmin)
      .filter(
        (d) =>
          !d.permissions?.length ||
          d.permissions.some((p) => this.authService.hasGroupPermission(p))
      )
      .filter((d) => !d.features?.length || d.features.every((f) => accountFeatures.includes(f)))
      .map((d) => {
        if (isExecutive && d.executiveFeatures?.length) {
          const hasAllFeatures = d.executiveFeatures.every((f) => accountFeatures.includes(f));
          if (!hasAllFeatures) {
            return {
              ...d,
              disabled: true,
              disabledReason: d.disabledReasonKey ? translate(d.disabledReasonKey) : undefined,
            };
          }
        }
        return d;
      });
    this.filteredTabBarData.set(filtered);

    const currentActiveTabId: string | undefined = this.activeTab()?.id;
    const nextActiveTabId: string | undefined = filtered.some((t) => t.id === currentActiveTabId)
      ? currentActiveTabId
      : filtered[0]?.id;

    if (nextActiveTabId) {
      this.onTabChange({ detail: { tab: { id: nextActiveTabId } } }, true);
    } else {
      this.activeTab.set(undefined);
      this.onPanelClose();
    }
  }

  onTabChange($event, isFirstChange?) {
    const tab = this.filteredTabBarData().find((t) => t.id === $event.detail.tab.id);
    this.activeTab.set(tab);

    if (!isFirstChange && !this.panelOpen() && tab.leftSideTemplate) {
      this.onPanelOpen();
    }

    if (!tab.leftSideTemplate) {
      this.onPanelClose();
    }
  }

  onPanelOpen() {
    if (!this.activeTab()?.leftSideTemplate) {
      return;
    }

    this.panelOpen.set(true);
    this.responsiveService.setIsSidePanelOpen(true);
    this.open.emit();
  }

  onPanelClose() {
    this.panelOpen.set(false);
    this.responsiveService.setIsSidePanelOpen(false);
    this.close.emit();
  }
}
